import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import useErrors from '~/hooks/useErrors';
import { MerchantData } from '~/types/payment';

import { PaymentApi } from '../types';

type UsePaymentIntentDataOptions = {
  paymentApi: PaymentApi;
  onCreateCardIntentError?: (errorMessage: string) => void;
};

type UsePaymentIntentDataReturn = {
  merchantData: MerchantData | null;
  reinitializePaymentIntent: () => Promise<void>;
};

export const usePaymentIntentData = ({
  paymentApi,
  onCreateCardIntentError,
}: UsePaymentIntentDataOptions): UsePaymentIntentDataReturn => {
  const { reportUserErrors } = useErrors();
  const { t } = useTranslation('general');

  const [merchantData, setMerchantData] = useState<MerchantData | null>(null);
  const paymentIntentDataAbortController = useRef<AbortController | null>(null);

  const initializePaymentIntent = useCallback(async (): Promise<void> => {
    try {
      paymentIntentDataAbortController.current?.abort(
        'initializePaymentIntent',
      );
      const newAbortController = new AbortController();
      paymentIntentDataAbortController.current = newAbortController;
      const country = await Api.getCountry();
      const data = await paymentApi.createPaymentIntentCard(country);
      if (!newAbortController.signal.aborted)
        setMerchantData({ ...data, paymentIntent: data.payment_intent });
    } catch (e: any) {
      const errorMessage = e.error || e.message || 'Error :(';
      const isPaymentError = e.message?.includes('createPaymentIntentCard');
      reportUserErrors({
        error: e,
        method: 'createPaymentIntentCard',
        userMessage: isPaymentError
          ? t(
              'error:create_payment_intent_card',
              'Card payment initiation failed. Please re-enter your details or',
            )
          : errorMessage,
      });

      onCreateCardIntentError && onCreateCardIntentError(errorMessage);
    }
  }, [paymentApi]);

  const reinitializePaymentIntent = useCallback(async (): Promise<void> => {
    setMerchantData(null);
    initializePaymentIntent();
  }, [initializePaymentIntent]);

  useEffect(() => {
    initializePaymentIntent();

    return (): void => {
      paymentIntentDataAbortController.current?.abort();
    };
  }, [initializePaymentIntent]);

  return { merchantData, reinitializePaymentIntent };
};
